// ===================================================
// OnboardingModal.js — 첫 방문자를 위한 온보딩 플로우 모달
//
// 역할:
//   - 앱 최초 실행 시 1회만 표시됩니다.
//   - 4개의 슬라이드로 구성됩니다:
//     · Step 1: 앱 소개 (옴살이 뭐야?)
//     · Step 2: 4가지 이야기 모드 소개
//     · Step 3: 주요 기능 (기억 되살리기, 나와의 대화)
//     · Step 4: 시작하기 CTA
//   - 완료 후 localStorage에 'omsal_onboarding_done' 저장
// ===================================================

import { useState } from 'react';
import './OnboardingModal.css';
import { appWrite } from './utils/appStorage';
import { MODE_CONFIG } from './content/modeConfig';

// 온보딩 완료 여부를 기록하는 localStorage 키
export const ONBOARDING_KEY = 'omsal_onboarding_done';

// ── 슬라이드 1: 앱 소개 ────────────────────────────────────────
function Step1() {
  return (
    <div className="ob-step">
      <div className="ob-step-icon">✏️</div>
      <h2 className="ob-step-title">반가워, 나는 옴살이야</h2>
      <p className="ob-step-desc">
        AI가 질문을 던지고, 네가 대답하다 보면<br />
        어느새 오늘의 이야기가 완성돼.
      </p>
      <p className="ob-step-desc secondary">
        일기, 메모, 고민 상담까지<br />
        대화하듯 기록해봐.
      </p>
    </div>
  );
}

// ── 슬라이드 2: 4가지 모드 소개 ────────────────────────────────
function Step2() {
  const modes = [
    { ...MODE_CONFIG.diary,    key: 'diary',    desc: '하루를 돌아보고 감정을 탐색해' },
    { ...MODE_CONFIG.note,     key: 'note',     desc: '생각이나 메모를 자유롭게 정리해' },
    { ...MODE_CONFIG.planner,  key: 'planner',  desc: '할 일을 정리하고 계획을 세워' },
    { ...MODE_CONFIG.decision, key: 'decision', desc: '고민 앞에서 생각을 풀어가봐' },
  ];

  return (
    <div className="ob-step">
      <h2 className="ob-step-title">4가지 이야기 모드</h2>
      <p className="ob-step-subtitle">기분과 목적에 맞게 골라봐</p>
      <div className="ob-mode-grid">
        {modes.map(m => (
          <div key={m.key} className="ob-mode-card">
            <span className="ob-mode-icon">{m.icon}</span>
            <span className="ob-mode-label">{m.label}</span>
            <span className="ob-mode-desc">{m.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 슬라이드 3: 주요 기능 소개 ────────────────────────────────
function Step3() {
  const features = [
    {
      icon: '💌',
      title: '기억 되살리기',
      desc: '오래된 기록을 AI가 꺼내 대화를 이어줘. 흘러간 순간도 다시 살아나.',
    },
    {
      icon: '💬',
      title: '나와의 대화',
      desc: '지금까지 쌓인 기록을 바탕으로, AI가 나만 아는 이야기를 나눠줘.',
    },
    {
      icon: '📝',
      title: '주간 회고',
      desc: '매주 일요일, 한 주를 돌아보는 따뜻한 메시지가 도착해.',
    },
  ];

  return (
    <div className="ob-step">
      <h2 className="ob-step-title">기록을 더 풍부하게</h2>
      <p className="ob-step-subtitle">이런 기능들이 기다리고 있어</p>
      <div className="ob-feature-list">
        {features.map((f, i) => (
          <div key={i} className="ob-feature-row">
            <span className="ob-feature-icon">{f.icon}</span>
            <div className="ob-feature-body">
              <span className="ob-feature-title">{f.title}</span>
              <span className="ob-feature-desc">{f.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 슬라이드 4: 시작하기 CTA ───────────────────────────────────
function Step4({ onDone }) {
  return (
    <div className="ob-step ob-step-final">
      <div className="ob-step-icon final">✨</div>
      <h2 className="ob-step-title">이제 이야기를 시작해볼까?</h2>
      <p className="ob-step-desc">
        첫 이야기는 어떤 것이든 좋아.<br />
        오늘 하루, 지금 이 순간부터.
      </p>
      <button className="ob-start-btn" onClick={onDone}>
        시작하기
      </button>
    </div>
  );
}

// ── 메인 모달 컴포넌트 ─────────────────────────────────────────
function OnboardingModal({ onDone }) {
  const [step, setStep] = useState(0);
  const TOTAL_STEPS = 4;

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
    } else {
      handleDone();
    }
  };

  const handleDone = () => {
    appWrite(ONBOARDING_KEY, 'true');
    onDone();
  };

  const steps = [
    <Step1 key={0} />,
    <Step2 key={1} />,
    <Step3 key={2} />,
    <Step4 key={3} onDone={handleDone} />,
  ];

  return (
    <div className="ob-overlay">
      <div className="ob-modal">

        {/* 슬라이드 내용 */}
        <div className="ob-content">
          {steps[step]}
        </div>

        {/* 하단 네비게이션 */}
        <div className="ob-footer">
          {/* 점 표시 (현재 위치) */}
          <div className="ob-dots">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={`ob-dot ${i === step ? 'active' : ''}`}
                onClick={() => setStep(i)}
              />
            ))}
          </div>

          {/* 다음 / 건너뛰기 버튼 (마지막 슬라이드 제외) */}
          {step < TOTAL_STEPS - 1 && (
            <div className="ob-nav-row">
              <button className="ob-skip-btn" onClick={handleDone}>
                건너뛰기
              </button>
              <button className="ob-next-btn" onClick={handleNext}>
                다음 →
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default OnboardingModal;
